import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Eye,
  Gauge,
  Info,
  PackageX,
  PlugZap,
  RadioTower,
  ShieldAlert,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { cn } from '@/lib/utils';
import {
  acknowledgeAlert,
  fetchAlerts,
  fetchAlertsSummary,
  fetchDetected,
  hoursAgo,
  type Alert,
  type AlertStatus,
  type Severity,
} from '@/lib/alerts-api';

/**
 * Alerty floty — DWA ŹRÓDŁA, świadomie rozdzielone, bo znaczą co innego.
 *
 * „Zgłoszone" to wiersze, które wstawia agent / konsola SmartSync. W v1 agenta nie ma, więc nic
 * nowego nie powstaje samo i portal mówi to wprost — inaczej pusta lista wygląda na „wszystko
 * w porządku", a znaczy „nikt nie patrzy".
 *
 * „Wykryte przez portal" liczymy TERAZ z read-modelu. To nie wiersze w bazie: nie mają statusu
 * i nie da się ich potwierdzić. Wrzucenie ich do jednej listy ze zgłoszonymi byłoby kłamstwem.
 */

const SEV_ICON = { CRITICAL: TriangleAlert, WARNING: AlertTriangle, INFO: Info } as const;

/**
 * PRÓG MILCZENIA — wartości muszą zgadzać się z `api/src/machines/agent-presence.ts`
 * (`DEFAULT_SILENT_MINUTES`, `MAX_AGENT_BACKOFF_MINUTES`), bo API i tak odrzuci wartość spoza
 * swojego zakresu. Powielone tutaj, żeby pole nie pozwalało wpisać liczby, o której z góry
 * wiadomo, że wróci błędem — a nie żeby ustanawiać drugą regułę.
 *
 * ⚠️ DOLNA GRANICA NIE JEST DOWOLNA: agent po awarii melduje się najrzadziej co kwadrans
 * (sufit narastającego odstępu), więc niższy próg nazywałby milczącym kogoś, z kim mamy kontakt.
 */
const DOMYSLNY_PROG_MILCZENIA = 20;
const MIN_PROG_MILCZENIA = 16;

function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useI18n();
  const Icon = SEV_ICON[severity];
  return (
    <Badge variant={severity === 'CRITICAL' ? 'destructive' : severity === 'WARNING' ? 'warning' : 'neutral'}>
      <Icon className="size-3" />
      {t(`alr.sev.${severity}`)}
    </Badge>
  );
}

function StatusBadge({ status }: { status: AlertStatus }) {
  const { t } = useI18n();
  if (status === 'RESOLVED')
    return (
      <Badge variant="success">
        <CheckCircle2 className="size-3" />
        {t('alr.status.RESOLVED')}
      </Badge>
    );
  if (status === 'ACKNOWLEDGED')
    return (
      <Badge variant="neutral" title={t('alr.status.ACKNOWLEDGED.hint')}>
        <Eye className="size-3" />
        {t('alr.status.ACKNOWLEDGED')}
      </Badge>
    );
  return (
    <Badge variant="warning">
      <AlertTriangle className="size-3" />
      {t('alr.status.OPEN')}
    </Badge>
  );
}

// ─────────────────────────── wykryte przez portal ────────────────────────────

function DetectedSection({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  /**
   * PRÓG W MINUTACH. Agent melduje się co minutę, a najrzadszy meldunek zdrowego agenta to
   * kwadrans (sufit narastającego odstępu po awarii), więc sensowne progi leżą MIĘDZY godzinami.
   * Wcześniej pole było w godzinach i najmniejsza wyrażalna wartość — jedna godzina — była
   * i tak trzykrotnie za duża.
   */
  const [silentMinutes, setSilentMinutes] = useState(DOMYSLNY_PROG_MILCZENIA);
  const [lowStockAt, setLowStockAt] = useState(1);

  const { data, isPending } = useQuery({
    queryKey: ['alerts', 'detected', groupId, silentMinutes, lowStockAt],
    queryFn: () => fetchDetected(groupId, silentMinutes, lowStockAt),
  });

  // sprzeczność liczy teraz API (`SILENT` + raportowane `online`), a nie strona po samym
  // `online` — bez agenta ta flaga nie ma skąd być prawdziwa i nie wolno z niej wnioskować
  const contradicting = (data?.silentMachines.rows ?? []).filter((m) => m.contradiction);

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">{t('alr.detectedTitle')}</h2>
          <p className="mt-1 text-xs text-muted-foreground">{t('alr.detectedNote')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="grid gap-1 text-xs">
            {t('alr.silentMinutes')}
            <Input
              type="number"
              min={MIN_PROG_MILCZENIA}
              max={43200}
              value={silentMinutes}
              onChange={(e) =>
                setSilentMinutes(Math.max(MIN_PROG_MILCZENIA, Number(e.target.value) || MIN_PROG_MILCZENIA))
              }
              className="w-24"
              title={t('alr.silentMinutesHint')}
            />
          </label>
          <label className="grid gap-1 text-xs">
            {t('alr.lowStockAt')}
            <Input
              type="number"
              min={0}
              max={9999}
              value={lowStockAt}
              onChange={(e) => setLowStockAt(Math.max(0, Number(e.target.value) || 0))}
              className="w-24"
            />
          </label>
        </div>
      </div>

      {isPending || !data ? (
        <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-md border border-border p-3">
            <p className="flex items-center gap-2 text-sm font-medium">
              <RadioTower className="size-4" />
              {t('alr.silentTitle')}
            </p>
            <p className="mt-1 text-2xl tabular-nums">
              {data.silentMachines.count}
              <span className="ml-1 text-sm text-muted-foreground">/ {data.silentMachines.outOf}</span>
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {data.silentMachines.rows.slice(0, 6).map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono">VM{m.vmid}</span>
                  <span className="truncate text-muted-foreground">{m.name}</span>
                  <span className="tabular-nums">
                    {m.hoursSilent === null ? t('alr.neverSeen') : t('alr.silentFor').replace('{n}', String(m.hoursSilent))}
                  </span>
                  {m.contradiction && (
                    <Badge variant="destructive" title={t('alr.contradictionHint')}>
                      {t('alr.contradiction')}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
            {contradicting.length > 0 && (
              <p className="mt-2 text-xs text-destructive">
                {t('alr.contradictionNote').replace('{n}', String(contradicting.length))}
              </p>
            )}
          </div>

          {/*
            LUKA WDROŻENIOWA obok awarii, nie w jednym worku. Ta kolumna istnieje, bo maszyny
            bez agenta były wcześniej liczone jako „milczące" i topiły prawdziwe awarie
            w liczbie, która rosła z każdym nowym automatem. Neutralny kolor jest celowy.
          */}
          <div className="rounded-md border border-border p-3">
            <p className="flex items-center gap-2 text-sm font-medium">
              <PlugZap className="size-4" />
              {t('alerts.unmonitored')}
            </p>
            <p className="mt-1 text-2xl tabular-nums">
              {data.unmonitoredMachines.count}
              <span className="ml-1 text-sm text-muted-foreground">/ {data.unmonitoredMachines.outOf}</span>
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {data.unmonitoredMachines.rows.slice(0, 6).map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-1.5">
                  <span className="font-mono">VM{m.vmid}</span>
                  <span className="truncate text-muted-foreground">{m.name}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">{t('alerts.unmonitoredHint')}</p>
          </div>

          <div className="rounded-md border border-border p-3">
            <p className="flex items-center gap-2 text-sm font-medium">
              <PackageX className="size-4" />
              {t('alr.lowStockTitle')}
            </p>
            <p className="mt-1 text-2xl tabular-nums">
              {data.lowStock.count}
              <span className="ml-1 text-sm text-muted-foreground">/ {data.lowStock.outOf}</span>
            </p>
            <ul className="mt-2 space-y-1 text-xs">
              {data.lowStock.rows.slice(0, 6).map((r) => (
                <li key={r.id} className="flex flex-wrap items-baseline gap-1.5">
                  <span className="font-mono">
                    VM{r.vmid}/{r.drawerNo}/{r.compartmentNo}
                  </span>
                  <span className="truncate text-muted-foreground">{r.item}</span>
                  <span className="tabular-nums">
                    {r.qtyCurrent}/{r.qtyMax}
                  </span>
                </li>
              ))}
            </ul>
            {data.lowStock.truncated && <p className="mt-2 text-xs text-[var(--warning)]">{t('alr.lowStockTruncated')}</p>}
          </div>

          <div className="rounded-md border border-border p-3">
            <p className="flex items-center gap-2 text-sm font-medium">
              <Gauge className="size-4" />
              {t('alr.rationingTitle')}
            </p>
            <p className="mt-1 text-2xl tabular-nums text-destructive">{data.rationing.blindPeople}</p>
            <p className="text-xs text-muted-foreground">{t('alr.blindPeople')}</p>
            <p className="mt-2 text-xs">
              {t('alr.rationingDetail')
                .replace('{over}', String(data.rationing.overPeople))
                .replace('{rules}', String(data.rationing.rulesWithBlind))}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">{t('alr.rationingWhy')}</p>
          </div>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────── strona ──────────────────────────────────────────

export function Alerts() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const toast = useToast();
  const { groupId, island } = useIslands();

  const [state, setState] = useState<DataTableState>({ search: '', page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' });
  const [severity, setSeverity] = useState<'' | Severity>('');
  const [status, setStatus] = useState<'' | AlertStatus>('');
  const [code, setCode] = useState('');
  const [acking, setAcking] = useState<string | null>(null);

  const summary = useQuery({
    queryKey: ['alerts', 'summary', groupId],
    queryFn: () => fetchAlertsSummary(groupId!),
    enabled: Boolean(groupId),
  });

  const params = useMemo(
    () => ({
      ...state,
      groupId: groupId ?? '',
      ...(severity ? { severity } : {}),
      ...(status ? { status } : {}),
      ...(code ? { code } : {}),
    }),
    [state, groupId, severity, status, code],
  );

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['alerts', 'list', params],
    queryFn: () => fetchAlerts(params),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  /** ⚠️ POTWIERDZAMY CAŁĄ GRUPĘ — wiersz na ekranie to n wierszy w bazie o tej samej treści. */
  const ack = useCallback(
    async (wiersz: Alert) => {
      if (!groupId) return;
      setAcking(wiersz.kluczGrupy);
      try {
        const r = await acknowledgeAlert(groupId, wiersz.code, wiersz.message);
        toast.success(t('alr.acked').replace('{n}', String(r.potwierdzono)));
        await qc.invalidateQueries({ queryKey: ['alerts'] });
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : t('common.error'));
      } finally {
        setAcking(null);
      }
    },
    [groupId, qc, t, toast],
  );

  const columns = useMemo<ColumnDef<Alert>[]>(
    () => [
      {
        id: 'createdAt',
        header: t('alr.when'),
        meta: { exportValue: (r) => r.createdAt },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs tabular-nums">
            {new Date(row.original.createdAt).toLocaleString('pl-PL')}
          </span>
        ),
      },
      {
        id: 'severity',
        header: t('alr.severity'),
        meta: { exportValue: (r) => r.severity },
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
      },
      {
        id: 'code',
        header: t('alr.code'),
        enableSorting: false,
        meta: { exportValue: (r) => r.code },
        cell: ({ row }) => (
          <span className="font-mono text-xs" title={t(`alr.code.${row.original.code}`)}>
            {row.original.code}
          </span>
        ),
      },
      {
        id: 'message',
        header: t('alr.message'),
        enableSorting: false,
        meta: { exportValue: (r) => r.message },
        /*
          ⚠️ LICZNIK STOI PRZY TREŚCI, nie w osobnej kolumnie — bo to ta sama informacja: „ten fakt
          zdarzył się n razy". Osobna kolumna z jedynką w każdym wierszu byłaby szumem.

          ⚠️ „od {kiedy}" POKAZUJEMY TYLKO PRZY GRUPIE. Zwinięcie n wierszy w jeden zabrałoby datę
          NAJSTARSZEGO wystąpienia, a to ona odpowiada na pytanie „od kiedy to trwa".
        */
        cell: ({ row }) => (
          <div className="grid gap-0.5">
            <span className="text-sm">
              {row.original.message}
              {row.original.licznik > 1 && (
                <Badge variant="neutral" className="ml-2 align-middle">
                  {t('alr.razy').replace('{n}', String(row.original.licznik))}
                </Badge>
              )}
            </span>
            {row.original.licznik > 1 && (
              <span className="text-[11px] text-muted-foreground">
                {t('alr.odKiedy').replace('{kiedy}', new Date(row.original.pierwszy).toLocaleString('pl-PL'))}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        header: t('alr.statusCol'),
        meta: { exportValue: (r) => r.status },
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) =>
          row.original.status === 'OPEN' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={acking === row.original.kluczGrupy}
              aria-label={`${t('alr.ack')} ${row.original.code}`}
              onClick={() => void ack(row.original)}
            >
              <Check />
              {row.original.licznik > 1 ? t('alr.ackGrupe').replace('{n}', String(row.original.licznik)) : t('alr.ack')}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ],
    [t, ack, acking],
  );

  const fetchAllRows = useCallback(async (): Promise<Alert[]> => {
    if (!groupId) return [];
    const out: Alert[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await fetchAlerts({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const s = summary.data;
  const channelAge = s?.newestAt ? hoursAgo(s.newestAt) : null;

  return (
    <div className="space-y-4">
      {/*
        Pusty kanał zgłoszeń nie znaczy „brak problemów" — znaczy „nikt nie zgłosił".

        ⚠️⚠️ TRZY STANY KOLORU (zgłoszone 01.09.2026): „jak wszystko dobrze, to na zielono, jak
        otwarte ostrzeżenie to na żółto jak teraz, jak krytyk to na czerwono". Wcześniej pasek był
        żółty także przy zerze otwartych — czyli kolor nie odróżniał spokoju od kłopotu.
      */}
      <div
        className={cn(
          'rounded-lg border p-4 text-sm',
          (s?.openBySeverity.CRITICAL ?? 0) > 0
            ? 'border-destructive/40 bg-destructive/10'
            : (s?.openBySeverity.WARNING ?? 0) > 0
              ? 'border-[var(--warning)]/40 bg-[var(--warning)]/10'
              : 'border-[var(--success)]/40 bg-[var(--success)]/10',
        )}
      >
        <p className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4" />
          {t('alr.bannerTitle')}
        </p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span className="tabular-nums">
            {t('alr.openCritical')}: <strong>{s?.openBySeverity.CRITICAL ?? 0}</strong>
          </span>
          <span className="tabular-nums">
            {t('alr.openWarning')}: <strong>{s?.openBySeverity.WARNING ?? 0}</strong>
          </span>
          <span className="tabular-nums text-muted-foreground">
            {t('alr.acknowledged')}: {s?.byStatus.ACKNOWLEDGED ?? 0} · {t('alr.resolved')}: {s?.byStatus.RESOLVED ?? 0}
          </span>
          {channelAge !== null && (
            <span className="tabular-nums">
              {t('alr.channelAge')}: <strong>{t('alr.hoursAgo').replace('{n}', String(channelAge))}</strong>
            </span>
          )}
        </div>
        <p className="mt-2 text-xs">{t('alr.bannerBody')}</p>
      </div>

      <DetectedSection groupId={groupId} />

      <DataTable<Alert>
        title={t('alr.reportedTitle')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.kluczGrupy}
        exportFileName={`alerty-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllRows}
        reportKey="alerts"
        reportGroupId={groupId}
        emptyText={t('alr.empty')}
        aktywneFiltry={[
          severity === '' ? null : t('alr.severity') + ': ' + t(`alr.sev.${severity}`),
          status === '' ? null : t('alr.statusCol') + ': ' + t(`alr.status.${status}`),
          code === '' ? null : t('alr.code') + ': ' + code,
        ].filter((x): x is string => x !== null)}
        onWyczyscFiltry={() => {
          setSeverity('');
          setStatus('');
          setCode('');
          onStateChange({ page: 1 });
        }}
        filters={
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="grid gap-1 text-xs">
              {t('alr.severity')}
              <Select
                value={severity}
                onChange={(e) => {
                  setSeverity(e.target.value as '' | Severity);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('alr.filterAll')}</option>
                <option value="CRITICAL">{t('alr.sev.CRITICAL')}</option>
                <option value="WARNING">{t('alr.sev.WARNING')}</option>
                <option value="INFO">{t('alr.sev.INFO')}</option>
              </Select>
            </label>
            <label className="grid gap-1 text-xs">
              {t('alr.statusCol')}
              <Select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as '' | AlertStatus);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('alr.filterAll')}</option>
                <option value="OPEN">{t('alr.status.OPEN')}</option>
                <option value="ACKNOWLEDGED">{t('alr.status.ACKNOWLEDGED')}</option>
                <option value="RESOLVED">{t('alr.status.RESOLVED')}</option>
              </Select>
            </label>
            <label className="grid gap-1 text-xs">
              {t('alr.code')}
              <Select
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  onStateChange({ page: 1 });
                }}
              >
                <option value="">{t('alr.filterAll')}</option>
                {s?.codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} ({c.count})
                  </option>
                ))}
              </Select>
            </label>
          </div>
        }
      />
    </div>
  );
}
